import type { ComponentPropsOptions, ExtractPublicPropTypes, MaybeRefOrGetter } from "vue";
import type { ThemeColor } from "@/colors/index";
import type { RoundedOption } from "@/components/helpers/common-props";
import type { FlexComponent } from "@/utils/render-component";
import { validateHotkey } from "@tanstack/hotkeys";
import { Primitive } from "reka-ui";
import { computed, defineComponent, ref } from "vue";
import Spinner from "@/components/Spinner";
import ShortcutHint from "@/components/ui/shortcut-hint/ShortcutHint";
import { isShortcutOverlayOpen } from "@/composables/shortcut-overlay";
import { useTheme } from "@/composables/theme";
import { useShortcut } from "@/composables/use-shortcut";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { renderIcon } from "@/utils/render-component";
import { normalizeShortcutKeys } from "@/utils/shortcuts";
import { buttonVariants } from "./variants";

export type Size = "xs" | "sm" | "md" | "lg";
export type Variant =
  | "outline"
  | "text"
  | "light"
  | "filled"
  | "subtle"
  | "dashed"
  | "secondary"
  | "ghost"
  | "link"
  | "flex";

export type ButtonType = "button" | "submit" | "reset";
export type Shape = "round" | "circle" | "square" | "default";

export const buttonProps = {
  label: p.string(),
  size: p.string<Size>("md"),
  variant: p.string<Variant>("filled"),
  shape: p.string<Shape>("default"),
  color: p.string<ThemeColor>(),
  disabled: p.boolean(),
  icon: p.custom<FlexComponent>([String, Object]),
  iconPlacement: p.string<"left" | "right">("left"),
  loading: p.boolean(),
  type: p.string<ButtonType>(),
  block: p.boolean(),
  tag: p.string<keyof HTMLElementTagNameMap>(),
  asChild: p.boolean(),
  radius: p.string<RoundedOption>("md"),
  shortcut: p.custom<
    | string[]
    | {
        keys: string[];
        avoidable?: boolean;
        /** Scope the shortcut to a specific element instead of the document. */
        target?: MaybeRefOrGetter<HTMLElement | Document | Window | null>;
        /**
         * Keep the shortcut armed while a provider modal or the palette is open.
         * Only a control rendered inside that overlay should set this.
         */
        overlayOwned?: boolean;
      }
  >([Array, Object]),
  onClick: p.func<() => void | Promise<void>>(),
  onPressed: p.func<() => void | Promise<void>>(),
  muted: p.boolean(),
} satisfies ComponentPropsOptions;

export type ButtonProps = ExtractPublicPropTypes<typeof buttonProps>;

const Button = defineComponent({
  name: "Button",
  props: buttonProps,
  setup(props, { slots, attrs }) {
    const theme = useTheme();
    const _loading = ref(false);
    const loading = computed(() => props.loading || _loading.value);

    const resolvedVariant = computed(() => {
      if (props.variant === "flex") {
        return theme.isDark.value ? "light" : "filled";
      }
      return props.variant;
    });

    const btnType = computed(() => {
      if (props.type) return props.type;
      if (["filled", "light"].includes(props.variant) || props.variant === "flex") return "submit";
      return "button";
    });

    async function onClick(_e?: MouseEvent) {
      if (loading.value || props.disabled) return;

      let handler = props.onPressed || props.onClick;

      if (Array.isArray(handler)) {
        handler = handler[1];
      }

      const result = handler?.();

      if (result instanceof Promise) {
        _loading.value = true;
        await result.finally(() => (_loading.value = false));
      }
    }

    if (props.shortcut) {
      const shortcutKeys = Array.isArray(props.shortcut) ? props.shortcut : props.shortcut.keys;
      const shortcutTarget = Array.isArray(props.shortcut) ? undefined : props.shortcut.target;
      const overlayOwned = Array.isArray(props.shortcut)
        ? false
        : (props.shortcut.overlayOwned ?? false);
      const ignoreInputs = Array.isArray(props.shortcut)
        ? props.shortcut.length === 1
        : (props.shortcut.avoidable ?? false);
      const valid =
        shortcutKeys.length > 0 && validateHotkey(normalizeShortcutKeys(shortcutKeys)).valid;

      if (valid) {
        useShortcut(
          () => (Array.isArray(props.shortcut) ? props.shortcut : (props.shortcut?.keys ?? [])),
          () => void onClick(),
          {
            ignoreInputs,
            target: shortcutTarget,
            overlayOwned,
            enabled: () =>
              !props.disabled && !loading.value && (overlayOwned || !isShortcutOverlayOpen.value),
          }
        );
      }
    }

    const shortcut = computed(() => {
      if (Array.isArray(props.shortcut)) return props.shortcut;
      return props.shortcut?.keys;
    });

    const hasContent = computed(() => !!slots.default || !!props.label);

    return () => (
      <Primitive
        as={props.asChild ? undefined : (props.tag ?? "button")}
        asChild={props.asChild}
        class={cn(
          buttonVariants({
            variant: resolvedVariant.value,
            size: props.size,
            shape: props.shape,
            radius: props.radius,
            block: props.block,
          }),
          props.icon && props.shape === "default" && hasContent.value
            ? props.iconPlacement === "right"
              ? "pr-2.5"
              : "pl-2.5"
            : "",
          attrs.class ?? ""
        )}
        {...({
          type: btnType.value,
          "data-accent": props.muted ? undefined : props.color,
          disabled: props.disabled || loading.value,
          onClick,
        } as any)}
      >
        {props.icon && props.iconPlacement === "left" && !loading.value && renderIcon(props.icon)}

        {hasContent.value && <span>{slots.default?.() || props.label}</span>}

        {!!shortcut.value?.length && props.shape !== "circle" && props.shape !== "square" && (
          <span
            class={cn(
              "center -mr-1 ml-0.5 space-x-0.5 rounded px-1 py-0.5 text-xs leading-none",
              !theme.isDark.value ? "bg-white/10 shadow-sm" : "bg-white/3 depth-10"
            )}
            data-accent={props.color}
          >
            <ShortcutHint keys={shortcut.value} />
          </span>
        )}

        {props.icon && props.iconPlacement === "right" && !loading.value && renderIcon(props.icon)}

        {loading.value && (
          <div class="bg-inherit center absolute inset-0 rounded-[inherit]">
            <Spinner class="h-4 w-4" />
          </div>
        )}
      </Primitive>
    );
  },
});

export default Button;
